import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { colors } from "../../styles/colors";
import { CustomPreview } from "../../components/CustomPreview/CustomPreview";
import { CustomSearchWithoutSolo } from "../../components/CustomSearchWithoutSolo/CustomSearchWithoutSolo";
import { CustomCreateSociety } from "../../containers/CustomCreateSociety/CustomCreateSociety";
import { ModalAddSociety } from "../../containers/CustomCreateSociety/ModalAddSociety/ModalAddSociety";
import { useMemo, useState } from "react";
import { isCompanyArchived, useGetAllCompanies } from "../../api/companies";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";

type Props = {};

/**
 * Les societes archivees restent dans la liste et la noient.
 *
 * Archiver ne supprime pas : la societe garde ses vehicules, ses factures et
 * ses membres, elle est seulement desactivee. Elles s'accumulent donc, et sur
 * un affichage en cartes elles occupent autant de place qu'une societe vivante
 * -- alors qu'on ne les consulte qu'exceptionnellement.
 *
 * Le defaut montre les actives seules : c'est ce qu'on vient chercher neuf fois
 * sur dix. Les deux autres choix restent a un clic, et le compteur annonce
 * toujours le total pour qu'on n'oublie pas que les archivees existent.
 */
type StatusFilter = "active" | "archived" | "all";

export const Compagnies: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { companies, isPending: loadingCompanies } = useGetAllCompanies();

  const [openModalAddSociety, setOpenModalAddSociety] =
    useState<boolean>(false);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [layout, setLayout] = useState<"cards" | "list">("cards");
  /*
   * Societe ouverte depuis le mode liste.
   *
   * Il n'existe pas de route /compagnies/:id -- la fiche societe est une
   * modale, ouverte depuis la carte. Le mode liste reutilise donc la MEME
   * modale plutot que d'inventer une page : deux chemins d'edition pour un
   * meme objet finiraient par diverger, et c'est l'edition qui compte, pas la
   * facon dont on y arrive.
   */
  const [editing, setEditing] = useState<TCompanie | null>(null);

  const counts = useMemo(() => {
    const archived = companies.filter((c) => isCompanyArchived(c.is_active)).length;
    return { total: companies.length, archived, active: companies.length - archived };
  }, [companies]);

  const visible = useMemo(() => {
    if (status === "all") return companies;
    const wantArchived = status === "archived";
    return companies.filter(
      (c) => isCompanyArchived(c.is_active) === wantArchived
    );
  }, [companies, status]);

  const handleClose = () => {
    setOpenModalAddSociety(false);
  };

  return (
    <>
      <CustomLoader isActive={loadingCompanies} />
      <ModalAddSociety
        compagnieInitial={{
          id: -1,
          name: "",
          registration_id: "",
          logo: "",
          is_fleet: 0,
          is_rental: 0,
        }}
        open={openModalAddSociety}
        onCancel={handleClose}
        onClose={handleClose}
      />

      {/*
        Edition depuis le mode liste, avec la meme modale que la carte.
        `key` force le remontage a chaque societe : sans lui, la modale garde
        l'etat de formulaire de la precedente et on edite la bonne societe avec
        les mauvaises valeurs pre-remplies.
      */}
      {editing && (
        <ModalAddSociety
          key={editing.id}
          compagnieInitial={editing}
          open={!!editing}
          onCancel={() => setEditing(null)}
          onClose={() => setEditing(null)}
        />
      )}
      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.containerHeader}>
          <CustomTypography
            variant="h2"
            text={t("compagnies.companyManagement", "Company management")}
            isUppercase
          />
          <CustomButtonTable
            variant={"secondary"}
            textVariant={"link"}
            label={t("compagnies.addACompany", "Add a company")}
            width={"fit-content"}
            fontWeight={"bold"}
            textDecoration={"none"}
            iconName={"plus"}
            padding={"0px 15px"}
            textTransform="uppercase"
            onClick={() => setOpenModalAddSociety(true)}
          />
        </div>
        <div className={classes.divApercu}>
          <CustomTypography
            text={t("common.preview", "Preview")}
            variant={"h5"}
            isUppercase
          />
          <div className={classes.divApercuFilter}>
            <div className={classes.divApercu2}>
              {!loadingCompanies && (
                <CustomPreview
                  label={`${t("compagnies.numberOfCompanies", {
                    nbUsers: counts.total,
                  })} `}
                  iconName="userCircle"
                  backgroundIcon={colors.secondary}
                />
              )}
            </div>
          </div>
        </div>
        <div className={classes.containerCompagnies}>
          <div className={classes.divTitle}>
            <CustomTypography
              text={t("compagnies.listOfCompanies", "List of companies")}
              variant={"h5"}
              isUppercase
            />

            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              {/*
                Le nombre est sur chaque choix : sans lui, "Archivees" est un
                pari, et on clique pour decouvrir que la liste est vide.
              */}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={status}
                onChange={(_e, value) => value && setStatus(value)}
              >
                <ToggleButton value="active">
                  {t("compagnies.filterActive", "Actives")} ({counts.active})
                </ToggleButton>
                <ToggleButton value="archived">
                  {t("compagnies.filterArchived", "Archivées")} ({counts.archived})
                </ToggleButton>
                <ToggleButton value="all">
                  {t("compagnies.filterAll", "Toutes")} ({counts.total})
                </ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={layout}
                onChange={(_e, value) => value && setLayout(value)}
              >
                <Tooltip title={t("compagnies.viewCards", "Cartes") as string}>
                  <ToggleButton value="cards">
                    <GridViewIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title={t("compagnies.viewList", "Liste") as string}>
                  <ToggleButton value="list">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>

              <CustomSearchWithoutSolo />
            </Stack>
          </div>

          {!loadingCompanies && visible.length === 0 && (
            <div className={classes.emptyState}>
              {status === "archived"
                ? t("compagnies.noArchived", "Aucune société archivée.")
                : t("compagnies.noCompany", "Aucune société à afficher.")}
            </div>
          )}

          {layout === "cards" ? (
            <div className={classes.divCompagnies}>
              {visible.map((companie) => (
                <CustomCreateSociety companie={companie} key={companie.id} />
              ))}
            </div>
          ) : (
            /*
              Mode liste : une ligne par societe.

              Les cartes conviennent quand on cherche un logo du coin de l'oeil ;
              elles deviennent penibles des qu'il s'agit de balayer trente noms
              ou de comparer des numeros d'immatriculation. Les deux repondent a
              des questions differentes, d'ou le choix laisse a l'utilisateur
              plutot qu'un remplacement.
            */
            <div className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("compagnies.name", "Société")}</TableCell>
                    <TableCell>
                      {t("compagnies.registrationId", "N° d'immatriculation")}
                    </TableCell>
                    <TableCell>{t("compagnies.type", "Type")}</TableCell>
                    <TableCell>{t("compagnies.status", "Statut")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((companie) => {
                    const archived = isCompanyArchived(companie.is_active);
                    return (
                      <TableRow
                        key={companie.id}
                        hover
                        sx={{ cursor: "pointer", opacity: archived ? 0.6 : 1 }}
                        onClick={() => setEditing(companie)}
                      >
                        <TableCell>
                          {companie.name}
                          <div className={classes.rowMuted}>#{companie.id}</div>
                        </TableCell>
                        <TableCell className={classes.rowMono}>
                          {companie.registration_id || "—"}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {Number(companie.is_fleet) === 1 && (
                              <Chip size="small" variant="outlined" label={t("compagnies.fleet", "Flotte")} />
                            )}
                            {Number(companie.is_rental) === 1 && (
                              <Chip size="small" variant="outlined" label={t("compagnies.rental", "Location")} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={archived ? "default" : "success"}
                            variant={archived ? "filled" : "outlined"}
                            label={
                              archived
                                ? t("compagnies.archived", "Archivée")
                                : t("compagnies.active", "Active")
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Sidebar>
    </>
  );
};
